import { useEffect, useMemo, useState } from "react";
import { PhoneCall } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { PrimaryButton } from "@/shared/components/ui/Button/PrimaryButton";
import SecondaryButton from "@/shared/components/ui/Button/SecondaryButton";
import CommonPhoneInput from "@/shared/components/ui/Input/CommonPhoneInput";
import CustomModal from "@/shared/components/ui/Modal/CustomModal";
import {
  DEFAULT_PHONE_COUNTRY_CODE,
  getPhoneNumberWithCountryCode,
  parseE164Phone,
} from "@/shared/utils/phoneCountryCodes";
import { showError, showErrorFromMutation } from "@/shared/utils/toast";
import { prepareVoiceCall } from "../api/voiceApi";
import { useCallStore } from "../stores/callStore";

const E164_RE = /^\+[1-9]\d{7,14}$/;
const FREE_STATUSES = ["idle", "ended", "failed"];

export default function CallButton({
  phoneNumber,
  relationContactId,
  contactName,
  label = "Call",
  variant = "primary",
  className = "",
  onSavePhone,
  onCallFinished,
  disabled = false,
}) {
  const [savedPhone, setSavedPhone] = useState("");
  const [isPhoneModalOpen, setIsPhoneModalOpen] = useState(false);
  const [phoneCountryCode, setPhoneCountryCode] = useState(DEFAULT_PHONE_COUNTRY_CODE);
  const [phoneNationalNumber, setPhoneNationalNumber] = useState("");
  const [isSavingPhone, setIsSavingPhone] = useState(false);
  const callStatus = useCallStore((state) => state.status);
  const startCall = useCallStore((state) => state.startCall);
  const lineBusy = !FREE_STATUSES.includes(callStatus);

  const { mutate: prepareCall, isPending } = useMutation({
    mutationFn: () => prepareVoiceCall(relationContactId),
    onSuccess: (prepared) =>
      startCall({
        callId: prepared.call_id,
        toNumber: prepared.to_number,
        contactName: prepared.contact_name || contactName,
        onFinished: onCallFinished,
      }),
    onError: (error) => showErrorFromMutation(error, "This call could not be started."),
  });

  const normalizedPhone = useMemo(
    () => String(savedPhone || phoneNumber || "").trim(),
    [phoneNumber, savedPhone],
  );
  const hasPhone = E164_RE.test(normalizedPhone);

  useEffect(() => {
    const parsed = parseE164Phone(phoneNumber);
    setSavedPhone("");
    setPhoneCountryCode(parsed.countryCode);
    setPhoneNationalNumber(parsed.nationalNumber);
  }, [phoneNumber]);

  const handleStart = () => {
    if (!hasPhone) {
      if (onSavePhone) setIsPhoneModalOpen(true);
      return;
    }
    prepareCall();
  };

  const handleSavePhone = async () => {
    const nextPhone = getPhoneNumberWithCountryCode(phoneCountryCode, phoneNationalNumber);
    if (!E164_RE.test(nextPhone)) {
      showError("Enter a valid phone number.");
      return;
    }
    setIsSavingPhone(true);
    try {
      await onSavePhone(nextPhone);
      setSavedPhone(nextPhone);
      setIsPhoneModalOpen(false);
      prepareCall();
    } catch {
      // The save mutation already shows the server error.
    } finally {
      setIsSavingPhone(false);
    }
  };

  const isDisabled = (!hasPhone && !onSavePhone) || disabled || isPending || lineBusy;
  const title = lineBusy ? "On a call" : label;

  return (
    <div className="flex flex-col gap-2">
      {variant === "secondary" ? (
        <SecondaryButton
          title={title}
          icon={<PhoneCall size={16} className="text-gray-500" />}
          disabled={isDisabled}
          isLoading={isPending}
          onClick={handleStart}
          className={className}
        />
      ) : (
        <PrimaryButton
          startContent={<PhoneCall size={16} />}
          disabled={isDisabled}
          isLoading={isPending}
          onClick={handleStart}
          className={className}
        >
          {title}
        </PrimaryButton>
      )}

      <CustomModal
        isOpen={isPhoneModalOpen}
        onClose={() => setIsPhoneModalOpen(false)}
        width="480px"
        className="p-6"
      >
        <div className="flex flex-col gap-5">
          <div>
            <h6 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
              Add phone number
            </h6>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              The number will be saved to this contact before the call starts. Only US numbers can
              be called right now.
            </p>
          </div>
          <CommonPhoneInput
            countryCode={phoneCountryCode}
            phoneNumber={phoneNationalNumber}
            onChange={({ countryCode, nationalNumber }) => {
              setPhoneCountryCode(countryCode);
              setPhoneNationalNumber(nationalNumber);
            }}
            disabled={isSavingPhone}
          />
          <div className="flex justify-end gap-3">
            <SecondaryButton
              title="Cancel"
              onClick={() => setIsPhoneModalOpen(false)}
              disabled={isSavingPhone}
            />
            <PrimaryButton
              startContent={false}
              onClick={handleSavePhone}
              isLoading={isSavingPhone}
              disabled={!phoneNationalNumber || isSavingPhone}
            >
              Save & Call
            </PrimaryButton>
          </div>
        </div>
      </CustomModal>
    </div>
  );
}
